/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import type { ModuleLayoutConfig } from '@coursera-web/cds-labs';
import {
  TESTIMONIAL_DISPLAY_STYLES$1 as TESTIMONIAL_DISPLAY_STYLES,
  TestimonialItem,
  TestimonialsModule,
} from '@coursera-web/cds-labs';

import type { PromoTemplateTestimonialCarouselFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import CustomTestimonialItem from 'bundles/unified-common/modules/testimonials/CustomTestimonialItem';

type Props = {
  content: PromoTemplateTestimonialCarouselFragment & ModuleLayoutConfig;
};

export function TestimonialsModuleRenderer({ content }: Props) {
  let convertedTestimonials: JSX.Element[] = [];

  if (content.displayStyle === TESTIMONIAL_DISPLAY_STYLES.CAROUSEL) {
    convertedTestimonials =
      content.testimonialsCollection?.items
        ?.map((testimonial, index) => {
          if (!testimonial) return null;
          const {
            author,
            authorOrganization,
            authorTitle,
            image,
            sys,
            testimonial: quote,
            associatedProduct,
            video,
          } = testimonial;
          return (
            <CustomTestimonialItem
              id={sys.id}
              key={sys.id}
              authorName={author}
              authorOrganization={authorOrganization?.name}
              authorTitle={authorTitle}
              image={image}
              quote={quote}
              productName={associatedProduct}
              videoEmbedUrl={video?.url}
              videoPlaceholderImage={video?.url ? image : undefined}
              trackingIndex={index}
            />
          );
        })
        .filter(Boolean) ?? [];
  } else {
    convertedTestimonials =
      content.testimonialsCollection?.items
        ?.map((testimonial, index) => {
          if (!testimonial) return null;
          const { author, authorTitle, image, sys, testimonial: quote } = testimonial;
          return (
            <TestimonialItem
              key={sys.id}
              name={author}
              imageSrc={image?.url}
              quote={quote}
              trackingIndex={index}
              learnerSince={Number(authorTitle?.match(/\d+/)?.[0])}
            />
          );
        })
        .filter(Boolean) ?? [];
  }

  const propsToChild = {
    testimonialItems: convertedTestimonials,
    headline: content.headline,
    displayStyle: content.displayStyle,
    preloadImage: content.preloadImage,
    removeTopPadding: content.removeTopPadding,
    lazyLoadImage: content.lazyLoadImage,
    backgroundColor: content.backgroundColor,
  };

  return <TestimonialsModule {...propsToChild} />;
}

export default TestimonialsModuleRenderer;
